import React, { useState, useEffect } from 'react';
import apiService from '../services/apiService';
import './AmazonImport.css';

function AmazonImport({ onImportSuccess }) {
  const [importStatus, setImportStatus] = useState({
    lastImport: null,
    lastSuccessAt: null,
    lastStatus: null,
    errorMessage: null,
    phasesDetail: null,
    canImport: true,
    nextImportIn: null,
    isLoading: false,
    currentPhase: null,
    importProgress: null
  });
  
  const [fileAnalysisResults, setFileAnalysisResults] = useState(null);
  const [isFileAnalysisLoading, setIsFileAnalysisLoading] = useState(false);

  useEffect(() => {
    checkImportStatus();
  }, []);

  const checkImportStatus = async () => {
    try {
      const status = await apiService.getImportStatus();
      setImportStatus(status);
    } catch (error) {
      console.error('Error checking import status:', error);
    }
  };

  const handleImportAmazon = async () => {
    if (!importStatus.canImport) {
      alert('Import Amazon non disponible. Veuillez attendre la fin de la période de 4 heures.');
      return;
    }

    setImportStatus(prev => ({ 
      ...prev, 
      isLoading: true, 
      currentPhase: 'Démarrage import unifié...',
      importProgress: { fbm: null, fba: null }
    }));
    
    try {
      // Phase FBM
      setImportStatus(prev => ({ 
        ...prev, 
        currentPhase: 'Import en cours... (FBM - Stock disponible)'
      }));
      
      console.log('🚀 Démarrage import Amazon SP-API unifié...');
      const result = await apiService.realAmazonImport();
      
      if (result.success) {
        const fbmCount = result.stats?.fbm?.updated || result.databaseUpdate?.fbm?.updated || 0;
        const fbaCount = result.stats?.fba?.updated || result.databaseUpdate?.fba?.updated || 0;
        const totalProducts = result.stats?.updated || result.databaseUpdate?.updated || 0;
        
        setImportStatus(prev => ({ 
          ...prev, 
          currentPhase: 'Import terminé !',
          importProgress: { 
            fbm: fbmCount, 
            fba: fbaCount,
            total: totalProducts
          }
        }));
        
        alert(`✅ Import Amazon unifié réussi!\n\n📦 FBM (Stock disponible): ${fbmCount} produits\n🏪 FBA + FC Transfer: ${fbaCount} produits\n📊 Total traité: ${totalProducts} produits\n\nNouveau calcul FC Transfer par différence Amazon activé!`);
        
        // Actualiser les données produits dans l'interface principale
        if (onImportSuccess) {
          console.log('🔄 Actualisation des données produits après import réussi...');
          await onImportSuccess();
        }
      } else {
        alert(`❌ Import Amazon échoué:\n\n${result.message || result.error}`);
      }
      
      // Refréchir le statut après l'import
      await checkImportStatus();
      
    } catch (error) {
      console.error('Error during import:', error);
      alert('Erreur lors de l\'import Amazon unifié');
      setImportStatus(prev => ({ 
        ...prev, 
        currentPhase: 'Erreur import',
        importProgress: null
      }));
    } finally {
      setTimeout(() => {
        setImportStatus(prev => ({ 
          ...prev, 
          isLoading: false,
          currentPhase: null,
          importProgress: null
        }));
      }, 3000);
    }
  };

  const handleTestConnection = async () => {
    setImportStatus(prev => ({ ...prev, isLoading: true }));
    
    try {
      console.log('🔗 Test connexion Amazon...');
      const result = await apiService.testAmazonConnection();
      
      if (result.success) {
        alert(`✅ Test connexion réussi!\n\n${result.message}`);
      } else {
        alert(`❌ Test connexion échoué:\n\n${result.message || result.error}`);
      }
      
    } catch (error) {
      console.error('Error during connection test:', error);
      alert('Erreur lors du test de connexion');
    } finally {
      setImportStatus(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleRefresh = async () => {
    setImportStatus(prev => ({ ...prev, isLoading: true }));
    
    try {
      // Recharger les données depuis SQLite
      await apiService.refreshData();
      alert('Données actualisées depuis la base SQLite');
      
      // Actualiser les données produits dans l'interface principale
      if (onImportSuccess) {
        console.log('🔄 Actualisation des données produits après refresh...');
        await onImportSuccess();
      }
      
      // Refréchir le statut
      await checkImportStatus();
      
    } catch (error) {
      console.error('Error refreshing data:', error);
      alert('Erreur lors de l\'actualisation des données');
    } finally {
      setImportStatus(prev => ({ ...prev, isLoading: false }));
    }
  };


  const handleFileAnalysis = async () => {
    setIsFileAnalysisLoading(true);
    setFileAnalysisResults(null);
    
    try {
      console.log('🔍 Démarrage analyse structure fichier Amazon FR+BE...');
      
      const response = await fetch('/api/test-analyze-file', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        setFileAnalysisResults(result);
        console.log(`✅ Analyse terminée: ${result.structure.totalColumns} colonnes, ${result.structure.totalLines} lignes`);
      } else {
        alert(`❌ Analyse fichier échouée:\n\n${result.error}\n\n${result.details || ''}`);
      }
      
    } catch (error) {
      console.error('Error during file analysis:', error);
      alert('Erreur lors de l\'analyse du fichier');
    } finally {
      setIsFileAnalysisLoading(false);
    }
  };

  const formatTimeRemaining = (minutes) => {
    if (!minutes || minutes <= 0) return null;
    
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return null;
    const now = new Date();
    const t = new Date(timestamp.includes('Z') ? timestamp : timestamp + 'Z');
    const diffMins = Math.floor((now - t) / (1000 * 60));
    if (diffMins < 60) return `il y a ${diffMins}min`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    if (hours < 24) return `il y a ${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
    const days = Math.floor(hours / 24);
    return `il y a ${days} jour${days > 1 ? 's' : ''}`;
  };

  const getSyncStatusBlock = () => {
    const { lastStatus, lastSuccessAt, errorMessage, phasesDetail } = importStatus;

    // Cas erreur totale : dernière tentative a échoué
    if (lastStatus === 'error') {
      const failedPhases = phasesDetail
        ? Object.entries(phasesDetail)
            .filter(([, v]) => v.status === 'error')
            .map(([phase, v]) => `${phase.toUpperCase()}: ${v.error}`)
        : null;
      return (
        <div className="sync-status sync-status--error">
          <span className="sync-status__icon">🔴</span>
          <span className="sync-status__text">
            Erreur lors du dernier import
            {failedPhases && failedPhases.length > 0 && (
              <ul className="sync-status__errors">
                {failedPhases.map((msg, i) => <li key={i}>{msg}</li>)}
              </ul>
            )}
            {!failedPhases && errorMessage && (
              <span className="sync-status__detail"> — {errorMessage}</span>
            )}
            {lastSuccessAt && (
              <span className="sync-status__last-ok"> Dernière synchro réussie : {formatTimestamp(lastSuccessAt)}</span>
            )}
          </span>
        </div>
      );
    }

    // Pas de succès récent (>24h ou jamais)
    const successHoursAgo = lastSuccessAt
      ? (new Date() - new Date(lastSuccessAt.includes('Z') ? lastSuccessAt : lastSuccessAt + 'Z')) / (1000 * 60 * 60)
      : null;

    if (!lastSuccessAt || successHoursAgo > 24) {
      const daysSinceSuccess = successHoursAgo ? Math.floor(successHoursAgo / 24) : null;
      return (
        <div className="sync-status sync-status--warning">
          <span className="sync-status__icon">⚠️</span>
          <span className="sync-status__text">
            {lastSuccessAt
              ? `Aucune synchro depuis ${daysSinceSuccess} jour${daysSinceSuccess > 1 ? 's' : ''}`
              : 'Aucune synchro réussie enregistrée'}
          </span>
        </div>
      );
    }

    // Succès récent
    const partial = lastStatus === 'partial';
    return (
      <div className={`sync-status sync-status--${partial ? 'warning' : 'success'}`}>
        <span className="sync-status__icon">{partial ? '⚠️' : '✅'}</span>
        <span className="sync-status__text">
          {partial ? 'Synchro partielle' : 'Dernière synchro réussie'} : {formatTimestamp(lastSuccessAt)}
          {partial && phasesDetail && (
            <ul className="sync-status__errors">
              {Object.entries(phasesDetail)
                .filter(([, v]) => v.status === 'error')
                .map(([phase, v]) => <li key={phase}>{phase.toUpperCase()}: {v.error}</li>)}
            </ul>
          )}
        </span>
      </div>
    );
  };

  return (
    <div className="amazon-import">
      <h2>Import Amazon API</h2>
      
      <div className="import-status">
        <div className="status-indicators">
          {getSyncStatusBlock()}

          {!importStatus.canImport && importStatus.nextImportIn && (
            <div className="status-item next-import">
              <strong>Prochain import dans :</strong> {formatTimeRemaining(importStatus.nextImportIn)}
            </div>
          )}
        </div>
      </div>

      <div className="import-actions">
        <button 
          className="import-amazon-btn"
          onClick={handleImportAmazon}
          disabled={!importStatus.canImport || importStatus.isLoading}
          title={!importStatus.canImport ? 'Import non disponible - période de 4h non écoulée' : 'Import unifié: FBM + FBA + FC Transfer en une fois'}
        >
          {importStatus.isLoading ? (importStatus.currentPhase || 'Import en cours...') : 'Import Amazon Unifié'}
        </button>

        <button 
          className="refresh-btn"
          onClick={handleRefresh}
          disabled={importStatus.isLoading}
          title="Actualiser les données depuis la base SQLite locale"
        >
          {importStatus.isLoading ? 'Actualisation...' : 'Actualiser'}
        </button>

        <button 
          className="test-btn"
          onClick={handleTestConnection}
          disabled={importStatus.isLoading}
          title="Tester la connexion Amazon SP-API"
        >
          {importStatus.isLoading ? 'Test...' : 'Test Connexion'}
        </button>


        <button 
          className="test-file-analysis-btn"
          onClick={handleFileAnalysis}
          disabled={isFileAnalysisLoading || importStatus.isLoading}
          title="Analyser structure fichier Amazon FR+BE combiné - un seul appel API"
          style={{
            backgroundColor: '#00b894',
            color: 'white',
            marginLeft: '10px'
          }}
        >
          {isFileAnalysisLoading ? '🔍 Analyse en cours...' : '🔍 Analyser Fichier'}
        </button>
      </div>

      {!importStatus.canImport && (
        <div className="import-restriction-notice">
          <h3>⚠️ Import limité</h3>
          <p>
            Les imports Amazon sont limités à une fois toutes les 4 heures pour respecter les limites de l'API Amazon.
            Vous pourrez effectuer le prochain import dans <strong>{formatTimeRemaining(importStatus.nextImportIn)}</strong>.
          </p>
          <p>
            Vous pouvez utiliser le bouton "Actualiser" pour recharger les données depuis la base locale à tout moment.
          </p>
        </div>
      )}

      {importStatus.isLoading && importStatus.currentPhase && (
        <div className="import-progress">
          <h3>📊 Import en cours</h3>
          <p><strong>Phase :</strong> {importStatus.currentPhase}</p>
          {importStatus.importProgress && (
            <div className="progress-details">
              {importStatus.importProgress.fbm !== null && (
                <p>✅ FBM (Stock disponible) : {importStatus.importProgress.fbm} produits</p>
              )}
              {importStatus.importProgress.fba !== null && (
                <p>✅ FBA + FC Transfer : {importStatus.importProgress.fba} produits</p>
              )}
              {importStatus.importProgress.total !== null && (
                <p><strong>Total traité : {importStatus.importProgress.total} produits</strong></p>
              )}
            </div>
          )}
        </div>
      )}


      {fileAnalysisResults && (
        <div className="file-analysis-results" style={{
          marginTop: '20px',
          padding: '15px',
          border: '2px solid #00b894',
          borderRadius: '8px',
          backgroundColor: '#f1fcf9'
        }}>
          <h3>🔍 Analyse Structure Fichier Amazon</h3>
          <div style={{ marginBottom: '15px' }}>
            <p><strong>Période :</strong> {fileAnalysisResults.period}</p>
            <p><strong>Report ID :</strong> {fileAnalysisResults.reportId}</p>
            <p><strong>Colonnes :</strong> {fileAnalysisResults.structure.totalColumns}</p>
            <p><strong>Lignes de données :</strong> {fileAnalysisResults.structure.totalLines}</p>
          </div>
          
          {/* Key columns section */}
          <div style={{ marginBottom: '15px' }}>
            <h4>🔑 Colonnes Clés Identifiées :</h4>
            <ul style={{ marginLeft: '20px' }}>
              {fileAnalysisResults.structure.keyColumns.sku && (
                <li><strong>SKU:</strong> {fileAnalysisResults.structure.keyColumns.sku.name} (col {fileAnalysisResults.structure.keyColumns.sku.index})</li>
              )}
              {fileAnalysisResults.structure.keyColumns.quantity && (
                <li><strong>Quantity:</strong> {fileAnalysisResults.structure.keyColumns.quantity.name} (col {fileAnalysisResults.structure.keyColumns.quantity.index})</li>
              )}
              {fileAnalysisResults.structure.keyColumns.marketplace ? (
                <li><strong>Marketplace:</strong> {fileAnalysisResults.structure.keyColumns.marketplace.name} (col {fileAnalysisResults.structure.keyColumns.marketplace.index})</li>
              ) : (
                <li><strong>Marketplace:</strong> ❌ Non trouvé</li>
              )}
              {fileAnalysisResults.structure.keyColumns.salesChannel ? (
                <li><strong>Sales Channel:</strong> {fileAnalysisResults.structure.keyColumns.salesChannel.name} (col {fileAnalysisResults.structure.keyColumns.salesChannel.index})</li>
              ) : (
                <li><strong>Sales Channel:</strong> ❌ Non trouvé</li>
              )}
            </ul>
          </div>

          {/* All headers section */}
          <div style={{ marginBottom: '15px' }}>
            <h4>📊 Toutes les Colonnes ({fileAnalysisResults.structure.totalColumns}) :</h4>
            <div style={{
              maxHeight: '200px',
              overflowY: 'auto',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '10px',
              fontSize: '12px',
              fontFamily: 'monospace'
            }}>
              {fileAnalysisResults.structure.headers.map((header, index) => (
                <div key={index} style={{ marginBottom: '2px' }}>
                  <strong>{header.index}:</strong> {header.name}
                </div>
              ))}
            </div>
          </div>

          {/* Sample data section */}
          {fileAnalysisResults.sampleData && fileAnalysisResults.sampleData.length > 0 && (
            <div style={{ marginBottom: '15px' }}>
              <h4>📋 Échantillon de Données (5 premières lignes) :</h4>
              <div style={{
                maxHeight: '300px',
                overflowY: 'auto',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                  <thead style={{ backgroundColor: '#f5f5f5', position: 'sticky', top: 0 }}>
                    <tr>
                      {Object.keys(fileAnalysisResults.sampleData[0]).slice(0, 8).map((key, index) => (
                        <th key={index} style={{ padding: '4px', borderBottom: '1px solid #ddd', textAlign: 'left', fontFamily: 'monospace' }}>
                          {key.length > 15 ? key.substring(0, 12) + '...' : key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fileAnalysisResults.sampleData.map((row, index) => (
                      <tr key={index} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f9f9f9' }}>
                        {Object.values(row).slice(0, 8).map((value, cellIndex) => (
                          <td key={cellIndex} style={{ padding: '4px', borderBottom: '1px solid #eee', fontFamily: 'monospace', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {String(value).length > 20 ? String(value).substring(0, 17) + '...' : String(value)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p style={{ fontSize: '11px', color: '#666', marginTop: '5px' }}>
                Seules les 8 premières colonnes sont affichées pour la lisibilité
              </p>
            </div>
          )}

          {/* Analysis section */}
          <div style={{ marginBottom: '15px' }}>
            <h4>📈 Analyse Distribution :</h4>
            <p><strong>Lignes traitées :</strong> {fileAnalysisResults.analysis.totalProcessed}</p>
            <p><strong>SKUs avec ventes :</strong> {fileAnalysisResults.analysis.salesSummary.totalSKUs}</p>
            
            {Object.keys(fileAnalysisResults.analysis.marketplaceDistribution).length > 0 && (
              <div>
                <p><strong>Distribution par marketplace/canal :</strong></p>
                <ul style={{ marginLeft: '20px' }}>
                  {Object.entries(fileAnalysisResults.analysis.marketplaceDistribution).map(([marketplace, count]) => (
                    <li key={marketplace}><strong>{marketplace}:</strong> {count} lignes</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* TOP 2 SKUs Validation section */}
          {fileAnalysisResults.analysis.top2SKUsValidation && fileAnalysisResults.analysis.top2SKUsValidation.length > 0 && (
            <div style={{ marginBottom: '15px' }}>
              <h4>🏆 TOP 2 SKUs (Validation Business) :</h4>
              <div style={{
                border: '1px solid #00b894',
                borderRadius: '8px',
                padding: '15px',
                backgroundColor: '#f8fffe'
              }}>
                {fileAnalysisResults.analysis.top2SKUsValidation.map((sku, index) => (
                  <div key={sku.sku} style={{ 
                    marginBottom: index < fileAnalysisResults.analysis.top2SKUsValidation.length - 1 ? '15px' : '0',
                    paddingBottom: index < fileAnalysisResults.analysis.top2SKUsValidation.length - 1 ? '15px' : '0',
                    borderBottom: index < fileAnalysisResults.analysis.top2SKUsValidation.length - 1 ? '1px solid #ddd' : 'none'
                  }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '16px' }}>
                      {index + 1}. {sku.sku}
                    </p>
                    <div style={{ marginLeft: '20px', lineHeight: '1.8' }}>
                      <p style={{ margin: '4px 0' }}>
                        <span style={{ marginRight: '15px' }}>🇫🇷 <strong>France:</strong> {sku.france} unités</span>
                      </p>
                      <p style={{ margin: '4px 0' }}>
                        <span style={{ marginRight: '15px' }}>🇧🇪 <strong>Belgique:</strong> {sku.belgique} unités</span>
                      </p>
                      <p style={{ margin: '4px 0' }}>
                        <span style={{ fontWeight: 'bold', color: '#00b894' }}>
                          📊 <strong>Total:</strong> {sku.total} unités ({sku.moyenneJour}/jour)
                        </span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <p style={{ 
                fontSize: '12px', 
                color: '#666', 
                marginTop: '10px',
                fontStyle: 'italic'
              }}>
                💡 Vérifiez si ces chiffres correspondent à votre réalité business avant intégration finale
              </p>
            </div>
          )}

          <button 
            onClick={() => setFileAnalysisResults(null)}
            style={{
              marginTop: '10px',
              padding: '5px 10px',
              backgroundColor: '#ccc',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Fermer
          </button>
        </div>
      )}

      <div className="import-info">
        <h3>ℹ️ Information</h3>
        <p><strong>Import Amazon Unifié :</strong> Récupère FBM + FBA + FC Transfer + Ventes en une seule opération (limité à 1 fois / 4h)</p>
        <ul style={{marginLeft: '20px'}}>
          <li><strong>Phase 1 :</strong> FBM (Stock disponible) depuis GET_MERCHANT_LISTINGS_ALL_DATA</li>
          <li><strong>Phase 2 :</strong> FBA + FC Transfer depuis GET_FBA_MYI_UNSUPPRESSED_INVENTORY_DATA</li>
          <li><strong>Phase 3 :</strong> Ventes FR+BE (7 jours) depuis GET_FLAT_FILE_ALL_ORDERS_DATA_BY_ORDER_DATE_GENERAL</li>
          <li><strong>Calculs :</strong> FC Transfer = afn-total - afn-fulfillable, Ventes/jour = total 7j / 7</li>
        </ul>
        <p><strong>Actualiser :</strong> Recharge les données depuis la base SQLite locale (illimité)</p>
        <p><strong>Test Connexion :</strong> Vérifie la configuration Amazon SP-API</p>
        <p><strong>🔍 Analyser Fichier :</strong> UN SEUL appel API FR+BE pour analyser la structure complète du fichier</p>
      </div>
    </div>
  );
}

export default AmazonImport;